import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, AlertCircle, Shield } from 'lucide-react';
import { useSpring, animated } from '@react-spring/web';
import logo from '/assets/logo.png';

interface HeroProps {
  setCurrentPage: (page: string) => void;
}

const Hero: React.FC<HeroProps> = ({ setCurrentPage }) => {
  const springProps = useSpring({
    from: { scale: 0.8, opacity: 0 },
    to: { scale: 1, opacity: 1 },
    config: { tension: 100, friction: 10 },
  });

  return (
    <div className="relative min-h-screen overflow-hidden hero-gradient">
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
      
      {/* Animated Circles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-accent/20 to-transparent rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '-2s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center">
          <animated.div style={springProps} className="inline-block mb-8">
            <img src={logo} alt="AstronShield" className="h-32 w-32 animate-glow" />
          </animated.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <span className="inline-block px-6 py-2 rounded-full bg-primary/10 border border-primary/20 text-lg md:text-xl font-medium text-primary">
              Innovating Defense, Inspiring Confidence
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-6xl md:text-8xl font-black mb-8"
          >
            <span className="text-gradient">Secure Your</span>
            <br />
            <span className="text-gradient">Digital Future</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-muted mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            Advanced cybersecurity solutions protecting your business from evolving threats in an interconnected world
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row justify-center gap-6"
          >
            <button 
              className="btn-primary group"
              onClick={() => setCurrentPage('get-started')}
            >
              <span className="flex items-center justify-center">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
            <button
              onClick={() => setCurrentPage('security-excellence')}
              className="px-8 py-4 glass-panel rounded-full font-semibold hover:bg-white/10 transition-all duration-300 group"
            >
              <span className="flex items-center justify-center">
                Security Excellence
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-32 grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {[
            {
              icon: Lock,
              title: "Advanced Protection",
              description: "Multi-layered security approach to protect your assets from sophisticated cyber threats"
            },
            {
              icon: AlertCircle,
              title: "24/7 Monitoring",
              description: "Continuous security monitoring and real-time threat detection"
            },
            {
              icon: Lock,
              title: "Incident Response",
              description: "Rapid response team ready to handle security incidents and minimize impact"
            }
          ].map((feature, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05, y: -10 }}
              className="glass-panel rounded-2xl p-8 backdrop-blur-xl border border-white/10 hover:border-primary/50 transition-colors"
            >
              <feature.icon className="h-12 w-12 mb-6 text-primary" />
              <h3 className="text-2xl font-semibold mb-4">{feature.title}</h3>
              <p className="text-muted leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Hero;